import React, { useState } from 'react';
import { useMenuPulse } from '../menu/useMenuPulse';
import { formatNavFigure } from './format-nav-figure';
import { navIndexMessages } from './nav-index-messages';
import { MOBILE_SUBTITLE_DOT, MOBILE_SUBTITLE_LINE, NAV_PART } from './nav-styles';
import type { PrimaryNavLocale } from '../primary-nav-links';
import type { MenuPulse } from '../../../server/routes/menu-pulse';
import type { IntlShape } from 'react-intl';
import { useCountUp } from '../../../utils/motion/use-count-up';
import { shouldRecountNavFiguresThisPageLoad } from '../../../utils/motion/nav-figure-recount';

/**
 * Mobile top-bar subtitle line under the wordmark (TEC navbar redesign Phase 1): a 6px live
 * dot + "{open} open jobs · {newToday} new today", figures in the display face like the strip's. Rendered only once the figures have loaded;
 * the wrapper always reserves the line's height so there's no layout shift when they arrive.
 */
export const MobileIndexSubtitle: React.FC<{
  locale: PrimaryNavLocale;
  formatMessage: IntlShape['formatMessage'];
  /** Phase 3 item 3.2: the server-rendered pulse (`pageContext.navPulse`), so this line already
   * has numbers on first paint instead of appearing blank until the client fetch resolves. */
  initialPulse?: MenuPulse | null;
  /** `newToday`: only "{n} new today" (the SRP, whose own head already gives the open-jobs total). */
  only?: 'newToday';
}> = ({ locale, formatMessage, initialPulse, only }) => {
  const pulse = useMenuPulse(locale, true, initialPulse);
  // Round 10: same number-roll treatment as IndexStrip.tsx's own four figures (see that file's
  // own comment for the full rationale). Both hooks are called unconditionally regardless of
  // `pulse` so the hook count never depends on it, matching IndexStrip's own approach.
  const [shouldRecount] = useState(() => shouldRecountNavFiguresThisPageLoad());
  const recountFor = (index: number) => (shouldRecount ? { enabled: true, fromRatio: 0.9, duration: 700, delayMs: index * 80 } : undefined);
  const displayedOpenJobs = useCountUp(pulse?.openJobs ?? 0, { disabled: !pulse, recountOnMount: recountFor(0) });
  const displayedNewToday = useCountUp(pulse?.newToday ?? 0, { disabled: !pulse, recountOnMount: recountFor(1) });
  return (
    <span className={MOBILE_SUBTITLE_LINE} data-jc-nav-part={NAV_PART.mobileSubtitle}>
      {pulse ? (
        <>
          <span className={MOBILE_SUBTITLE_DOT} aria-hidden />
          <span>
            {only !== 'newToday' ? (
              <>
                <b>{formatNavFigure(displayedOpenJobs, locale)}</b> {formatMessage(navIndexMessages.openJobs)}
                {' · '}
              </>
            ) : null}
            <b>{formatNavFigure(displayedNewToday, locale)}</b> {formatMessage(navIndexMessages.newToday)}
          </span>
        </>
      ) : null}
    </span>
  );
};
